import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getUserProfile, getHoldings, getPositions, getOrders, checkSessionStatus, testKiteToken, getQuote } from '../services/api';
import { getStockConfig, addStockConfig } from '../services/api';
import { getAutoPilotStatus, stopAutoPilot } from '../services/api';
import { removeToken, getStoredToken } from '../utils/tokenStorage';
import { DisableDashboardPage } from '../config';

import PortfolioView from '../components/PortfolioView';
import ContractNoteGrid from '../components/ContractNoteGrid';
import AutoPilotModal from '../components/AutoPilotModal';
import HomePage       from './HomePage';
import ProfilePage   from './ProfilePage';
import ConfigPage    from './ConfigPage';
import AlgosPage     from './AlgosPage';
import AnalysisPage   from './AnalysisPage';
import DownloadsPage  from './DownloadsPage';
import LogsPage       from './LogsPage';
import './Dashboard.css';

const ADMIN_SUBTABS = [
  { key: 'downloads',      label: 'Downloads'      },
  { key: 'configurations', label: 'Configurations' },
  { key: 'logs',           label: 'Logs'           },
];
const TABS = [
  { key: 'home',           label: 'Home'           },
  { key: 'dashboard',      label: 'Dashboard'      },
  { key: 'analysis',       label: 'Analysis'       },
  { key: 'algos',          label: 'Algos'          },
  { key: 'administration', label: 'Administration', subTabs: ADMIN_SUBTABS },
];
const visibleTabs = DisableDashboardPage ? TABS.filter(t => t.key !== 'dashboard') : TABS;

function Dashboard({ onLogout }) {
  const [activeTab, setActiveTabState]   = useState(() => {
    const stored = localStorage.getItem('jt-activeTab') || 'home';
    // Migrate a pre-Administration stored value (Downloads/Configurations/Logs
    // used to be top-level tabs) onto the new 'administration' parent tab.
    if (ADMIN_SUBTABS.some(s => s.key === stored)) return 'administration';
    return (DisableDashboardPage && stored === 'dashboard') ? (visibleTabs[0]?.key || 'home') : stored;
  });
  const [activeSubTab, setActiveSubTabState] = useState(() => {
    const storedTab = localStorage.getItem('jt-activeTab');
    if (ADMIN_SUBTABS.some(s => s.key === storedTab)) return storedTab;
    return localStorage.getItem('jt-activeSubTab') || 'downloads';
  });
  const [portfolio, setPortfolio]   = useState(null);
  const [holdings, setHoldings]     = useState([]);
  const [holdingLtps, setHoldingLtps] = useState({});
  const [positions, setPositions]   = useState([]);
  const [orders, setOrders]         = useState([]);
  const [contractNotes, setContractNotes] = useState([]);
  const [intradayHistory, setIntradayHistory] = useState([]);
  const [totalCharges, setTotalCharges] = useState(0);
  const [loading, setLoading]       = useState(true);
  const [darkMode, setDarkMode]     = useState(() => localStorage.getItem('jt-theme') === 'dark');
  const [menuOpen, setMenuOpen]         = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [configuredSymbols, setConfiguredSymbols] = useState(new Set());
  const [sessionValid, setSessionValid] = useState(true);
  const [showReauthPrompt, setShowReauthPrompt] = useState(false);
  const [autoPilotActive, setAutoPilotActive] = useState(false);
  const [showAutoPilotModal, setShowAutoPilotModal] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [holdingsPage, setHoldingsPage] = useState(1);
  const menuRef         = useRef(null);
  const mobileNavRef    = useRef(null);
  const kiteAuthFailed  = useRef(false);  // stops retry loops on Kite 400/401

  // Helper function to check if auto-refresh should happen (weekdays, 9:10 AM - 3:40 PM IST)
  const shouldAutoRefresh = useCallback(() => {
    const now = new Date();
    // Convert UTC to IST (UTC+5:30)
    const istTime = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
    const day = istTime.getUTCDay(); // 0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday
    const hours = istTime.getUTCHours();
    const minutes = istTime.getUTCMinutes();
    const timeInMinutes = hours * 60 + minutes;

    // Check if weekday (Monday=1 to Friday=5)
    const isWeekday = day >= 1 && day <= 5;
    // Check if between 9:10 AM (550 minutes) and 3:40 PM (940 minutes)
    const isWithinTradingHours = timeInMinutes >= 550 && timeInMinutes <= 940;

    return isWeekday && isWithinTradingHours;
  }, []);

  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    localStorage.setItem('jt-activeTab', tab);
  };

  const setActiveSubTab = (sub) => {
    setActiveSubTabState(sub);
    localStorage.setItem('jt-activeSubTab', sub);
  };

  useEffect(() => { localStorage.setItem('jt-theme', darkMode ? 'dark' : 'light'); }, [darkMode]);

  // Check session status on mount and every 5 minutes
  useEffect(() => {
    const checkSession = async () => {
      try {
        const token = getStoredToken();
        if (!token) return;
        const response = await checkSessionStatus();
        setSessionValid(response.valid);
        if (response.valid) {
          kiteAuthFailed.current = false;   // token renewed — resume polling
        } else {
          setShowReauthPrompt(true);
        }
      } catch (err) {
        console.error('Session check failed:', err);
      }
    };

    checkSession();
    const interval = setInterval(checkSession, 5 * 60 * 1000); // Check every 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Check Auto Pilot status on mount and periodically — reflects it across every page
  useEffect(() => {
    const checkAutoPilot = async () => {
      try {
        const token = getStoredToken();
        if (!token) return;
        const response = await getAutoPilotStatus();
        setAutoPilotActive(!!response.active);
      } catch (err) {
        console.error('Auto Pilot status check failed:', err);
      }
    };

    checkAutoPilot();
    const interval = setInterval(checkAutoPilot, 60 * 1000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const handleAutoPilotStarted = () => {
    setShowAutoPilotModal(false);
    setAutoPilotActive(true);
  };

  const handleExitAutoPilot = async () => {
    if (!window.confirm('Exit Auto Pilot? All Auto Pilot data will be erased and it will stop placing orders. Positions will need to be managed manually until Auto Pilot is started again.')) return;
    try {
      await stopAutoPilot();
      setAutoPilotActive(false);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to exit Auto Pilot');
    }
  };

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (mobileNavRef.current && !mobileNavRef.current.contains(e.target)) setMobileNavOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetches historical intraday data (past days only). Today's row is derived live from positions + contract notes.
  const loadIntradayHistory = async () => {
    const headers = { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` };
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/intraday`, { headers });
    if (!res.ok) return [];
    const data = await res.json();
    return (data.history || []).filter(h => h.date !== new Date().toISOString().split('T')[0]);
  };

  const refreshHoldingLtps = useCallback(async (holdingsArr) => {
    if (!holdingsArr || !holdingsArr.length) return;
    try {
      const symbols = holdingsArr.map(h => h.tradingsymbol);
      const quotes = await getQuote(symbols);
      const ltpMap = {};
      for (const sym of symbols) {
        if (quotes[sym]?.lastPrice != null) ltpMap[sym] = quotes[sym].lastPrice;
      }
      setHoldingLtps(ltpMap);
    } catch { /* keep existing last_price */ }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Profile is needed for the header's account menu on every tab, so it's
        // fetched regardless of DisableDashboardPage.
        const profile = await getUserProfile().catch(() => null);
        if (profile) setPortfolio(profile.data);

        if (DisableDashboardPage) return;

        // Fetch contract notes from backend
        let contractNotesData = { contractNotes: [], summary: { totalCharges: 0 } };
        try {
          const notesRes = await fetch(`${process.env.REACT_APP_API_URL}/api/intraday/contract-notes`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
          });
          if (notesRes.ok) contractNotesData = await notesRes.json();
        } catch (err) {
          console.warn('Could not fetch contract notes:', err.message);
        }

        const [holdingsData, positionsData, ordersData, configData] = await Promise.all([
          getHoldings().catch(() => null),
          getPositions().catch(() => null),
          getOrders().catch(() => null),
          getStockConfig().catch(() => ({ stocks: [] })),
        ]);
        const loadedHoldings = holdingsData?.data || [];
        setHoldings(loadedHoldings);
        refreshHoldingLtps(loadedHoldings);
        if (positionsData) setPositions(positionsData.data?.net || []);
        if (ordersData)   setOrders(ordersData.data || []);
        setConfiguredSymbols(new Set(((configData || {}).stocks || []).map(s => s.symbol)));
        setContractNotes(contractNotesData.contractNotes || []);
        setTotalCharges(contractNotesData.summary?.totalCharges || 0);

        // Load intraday history from backend API
        try {
          setIntradayHistory(await loadIntradayHistory());
        } catch (err) {
          console.warn('Failed to load intraday history:', err.message);
        }
      } finally { setLoading(false); }
    };
    loadData();
  }, [refreshHoldingLtps]);

  // Helper: handle a Kite auth failure (400/401) from any polling call
  const handleKiteAuthError = useCallback((status) => {
    if (status === 400 || status === 401) {
      kiteAuthFailed.current = true;
      setSessionValid(false);
      setShowReauthPrompt(true);
    }
  }, []);

  // Auto-refresh for Holdings (5 seconds)
  useEffect(() => {
    if (DisableDashboardPage) return;
    const interval = setInterval(async () => {
      if (!shouldAutoRefresh() || kiteAuthFailed.current) return;
      try {
        const holdingsData = await getHoldings();
        const h = holdingsData.data || [];
        setHoldings(h);
        refreshHoldingLtps(h);
      } catch (err) {
        handleKiteAuthError(err.response?.status);
        console.warn('Failed to refresh holdings:', err.message);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [shouldAutoRefresh, refreshHoldingLtps, handleKiteAuthError]);

  // Auto-refresh for Positions (1 second)
  useEffect(() => {
    if (DisableDashboardPage) return;
    const interval = setInterval(async () => {
      if (!shouldAutoRefresh() || kiteAuthFailed.current) return;
      try {
        const positionsData = await getPositions();
        setPositions(positionsData.data?.net || []);
      } catch (err) {
        handleKiteAuthError(err.response?.status);
        console.warn('Failed to refresh positions:', err.message);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [shouldAutoRefresh, handleKiteAuthError]);

  // Auto-refresh for Contract Notes (1 second)
  useEffect(() => {
    if (DisableDashboardPage) return;
    const interval = setInterval(async () => {
      if (!shouldAutoRefresh() || kiteAuthFailed.current) return;
      try {
        const notesRes = await fetch(`${process.env.REACT_APP_API_URL}/api/intraday/contract-notes`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
        });
        if (notesRes.status === 400 || notesRes.status === 401) {
          handleKiteAuthError(notesRes.status);
          return;
        }
        if (notesRes.ok) {
          const data = await notesRes.json();
          setContractNotes(data.contractNotes || []);
          setTotalCharges(data.summary?.totalCharges || 0);
        }
      } catch (err) {
        // Network errors (CORS/504) — log quietly, don't spam console
        console.warn('Failed to refresh contract notes:', err.message);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [shouldAutoRefresh, handleKiteAuthError]);

  // Auto-refresh for Orders (1 minute)
  useEffect(() => {
    if (DisableDashboardPage) return;
    const interval = setInterval(async () => {
      if (!shouldAutoRefresh() || kiteAuthFailed.current) return;
      try {
        const ordersData = await getOrders();
        setOrders(ordersData.data || []);
      } catch (err) {
        handleKiteAuthError(err.response?.status);
        console.warn('Failed to refresh orders:', err.message);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [shouldAutoRefresh, handleKiteAuthError]);

  // Manual refresh functions
  const refreshHoldings = async () => {
    if (DisableDashboardPage) return;
    try {
      const holdingsData = await getHoldings();
      setHoldings(holdingsData.data || []);
    } catch (err) {
      console.warn('Failed to refresh holdings:', err.message);
    }
  };

  const refreshPositions = async () => {
    if (DisableDashboardPage) return;
    try {
      const positionsData = await getPositions();
      setPositions(positionsData.data?.net || []);
    } catch (err) {
      console.warn('Failed to refresh positions:', err.message);
    }
  };

  const refreshIntraday = async () => {
    if (DisableDashboardPage) return;
    try {
      setIntradayHistory(await loadIntradayHistory());
    } catch (err) {
      console.warn('Failed to refresh intraday history:', err.message);
    }
  };

  // Today's row is always live: P&L from positions grid, charges from contract notes grid.
  // Past days come from the backend history file.
  const displayHistory = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const todayPnl = positions.reduce((s, p) => s + (p.pnl || 0), 0);
    const todayRow = { date: today, pnl: Math.round(todayPnl * 100) / 100, charges: Math.round(totalCharges * 100) / 100 };
    return [todayRow, ...intradayHistory];
  }, [positions, totalCharges, intradayHistory]);

  // Persist today's P&L to disk every 5 minutes so data survives PM2 restarts and reauthentication.
  // Only saves when there is something to save (non-zero P&L or charges).
  useEffect(() => {
    if (DisableDashboardPage) return;
    const save = async () => {
      const todayPnl = positions.reduce((s, p) => s + (p.pnl || 0), 0);
      if (todayPnl === 0 && totalCharges === 0) return;
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/api/intraday/save-today`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pnl: Math.round(todayPnl * 100) / 100, charges: Math.round(totalCharges * 100) / 100 }),
        });
      } catch { /* silent — non-critical background save */ }
    };
    const interval = setInterval(save, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [positions, totalCharges]);

  const refreshContractNotes = async () => {
    if (DisableDashboardPage) return;
    try {
      const notesRes = await fetch(`${process.env.REACT_APP_API_URL}/api/intraday/contract-notes`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` }
      });
      if (notesRes.ok) {
        const data = await notesRes.json();
        setContractNotes(data.contractNotes || []);
        setTotalCharges(data.summary?.totalCharges || 0);
      }
    } catch (err) {
      console.warn('Failed to refresh contract notes:', err.message);
    }
  };

  const refreshOrders = async () => {
    if (DisableDashboardPage) return;
    try {
      const ordersData = await getOrders();
      setOrders(ordersData.data || []);
    } catch (err) {
      console.warn('Failed to refresh orders:', err.message);
    }
  };

  const handleAddToConfig = async ({ symbol, name, exchange, type }) => {
    if (configuredSymbols.has(symbol)) return;
    try {
      const res = await addStockConfig({ symbol, name, exchange, type });
      setConfiguredSymbols(new Set((res.stocks || []).map(s => s.symbol)));
    } catch (_) {}
  };

  const handleLogout = () => { removeToken(); onLogout(); };

  const handleTestToken = async () => {
    setTestLoading(true);
    try {
      const result = await testKiteToken();
      setTestResult(result);
    } catch (err) {
      setTestResult({
        success: false,
        message: err.message,
        error: err.message
      });
    } finally {
      setTestLoading(false);
    }
  };

  const netBalance    = holdings.reduce((s, h) => { const ltp = holdingLtps[h.tradingsymbol] ?? h.last_price; return s + (((h.quantity || 0) + (h.t1_quantity || 0)) * ltp); }, 0);
  const holdingsPnL   = holdings.reduce((s, h) => { const qty = (h.quantity || 0) + (h.t1_quantity || 0); const ltp = holdingLtps[h.tradingsymbol] ?? h.last_price; return s + (h.pnl ?? ((ltp - h.average_price) * qty)); }, 0);
  const netPnl        = positions.reduce((s, p) => s + (p.pnl || 0), 0);
  const tillDatePnl   = displayHistory.reduce((sum, h) => sum + (h.pnl || 0) - (h.charges || 0), 0);

  if (loading) {
    return (
      <div className="dashboard-loading" data-theme={darkMode ? 'dark' : 'light'}>
        Loading portfolio…
      </div>
    );
  }

  return (
    <div className="dashboard" data-theme={darkMode ? 'dark' : 'light'}>
      <header className="dashboard-header">
        <div className="header-brand">
          <span className="header-title">JAAR Trade</span>
          <span className="header-sub">by JAARSolutions.co.in</span>
        </div>
        <div className="header-actions">
          <label
            className="autopilot-switch"
            title={autoPilotActive ? 'Stop Auto Pilot and manage positions manually' : 'Hand all open positions to Auto Pilot'}
          >
            <span className="autopilot-switch-text">Autopilot</span>
            <span className={`autopilot-switch-track ${(autoPilotActive || showAutoPilotModal) ? 'on' : ''}`}>
              <input
                type="checkbox"
                checked={autoPilotActive || showAutoPilotModal}
                onChange={() => (autoPilotActive ? handleExitAutoPilot() : setShowAutoPilotModal(true))}
              />
              <span className="autopilot-switch-thumb" />
            </span>
          </label>
          <div className="session-status">
            <span className={`status-indicator ${sessionValid ? 'valid' : 'expired'}`} title={sessionValid ? 'Kite session active' : 'Kite session expired'}>
              {sessionValid ? '🟢' : '🔴'}
            </span>
            {!sessionValid && (
              <button className="reauth-btn" onClick={() => setShowReauthPrompt(true)} title="Re-authenticate with Kite">
                Re-auth
              </button>
            )}
          </div>
          <button className="theme-toggle" onClick={() => setDarkMode(d => !d)} title="Toggle theme">
            {darkMode ? '☀️' : '🌙'}
          </button>
          <div className="user-menu" ref={menuRef}>
            <button className="user-menu-btn" onClick={() => setMenuOpen(o => !o)}>
              <span className="user-name">{portfolio?.user_name || 'Account'}</span>
              <span className={`menu-chevron ${menuOpen ? 'open' : ''}`}>▾</span>
            </button>
            {menuOpen && (
              <div className="user-dropdown">
                <button className="dropdown-item" onClick={() => { setActiveTab('profile'); setMenuOpen(false); }}>
                  👤 Profile
                </button>
                <button className="dropdown-item" onClick={handleTestToken} disabled={testLoading}>
                  {testLoading ? '⏳ Testing...' : '🧪 Test Kite Token'}
                </button>
                <div className="dropdown-divider" />
                <button className="dropdown-item danger" onClick={handleLogout}>⎋ Logout</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile hamburger nav — visible only on mobile via CSS */}
      <div className="mobile-nav-bar" ref={mobileNavRef}>
        <button
          className={`hamburger-btn ${mobileNavOpen ? 'open' : ''}`}
          onClick={() => setMobileNavOpen(o => !o)}
          aria-label="Navigation menu"
        >
          <span className="hb-line" />
          <span className="hb-line" />
          <span className="hb-line" />
        </button>
        {!DisableDashboardPage && (
          <span className={`mobile-pnl ${tillDatePnl >= 0 ? 'positive' : 'negative'}`}>
            ₹{new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(tillDatePnl)}
          </span>
        )}
        {mobileNavOpen && (
          <div className="mobile-nav-dropdown">
            {visibleTabs.map(({ key, label, subTabs }) => subTabs ? (
              <div key={key} className="mobile-nav-group">
                <div className="mobile-nav-item mobile-nav-group-label">{label}</div>
                {subTabs.map(sub => (
                  <button
                    key={sub.key}
                    className={`mobile-nav-item mobile-nav-subitem ${activeTab === key && activeSubTab === sub.key ? 'active' : ''}`}
                    onClick={() => { setActiveTab(key); setActiveSubTab(sub.key); setMobileNavOpen(false); }}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>
            ) : (
              <button
                key={key}
                className={`mobile-nav-item ${activeTab === key ? 'active' : ''}`}
                onClick={() => { setActiveTab(key); setMobileNavOpen(false); }}
              >
                {label}
              </button>
            ))}
            <div className="mobile-nav-divider" />
            <button
              className={`mobile-nav-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => { setActiveTab('profile'); setMobileNavOpen(false); }}
            >
              Profile
            </button>
          </div>
        )}
      </div>

      {/* Desktop tab bar — hidden on mobile via CSS */}
      <div className="dashboard-tabs">
        <div className="tabs-left">
          {visibleTabs.map(({ key, label }) => (
            <button key={key} className={`tab-button ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>
              {label}
            </button>
          ))}
        </div>
        <div className="tabs-right">
          {!DisableDashboardPage && (
            <span className={`menu-net-value ${tillDatePnl >= 0 ? 'positive' : 'negative'}`}>
              ₹{new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(tillDatePnl)}
            </span>
          )}
        </div>
      </div>

      {activeTab === 'administration' && (
        <div className="dashboard-subtabs">
          {ADMIN_SUBTABS.map(({ key, label }) => (
            <button key={key} className={`subtab-button ${activeSubTab === key ? 'active' : ''}`} onClick={() => setActiveSubTab(key)}>
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="dashboard-content">
        {activeTab === 'home' && <HomePage />}
        {!DisableDashboardPage && activeTab === 'dashboard' && portfolio && (
          <>
            {(() => {
              const sortedOrders = [...orders].reverse();
              return <PortfolioView profile={portfolio} orders={sortedOrders} ordersPage={ordersPage} onOrdersPageChange={setOrdersPage} positions={positions} intradayHistory={displayHistory}
                netBalance={0} netPnl={netPnl}
                configuredSymbols={configuredSymbols} onAddToConfig={handleAddToConfig} tillDatePnl={tillDatePnl}
                onRefreshOrders={refreshOrders} onRefreshPositions={refreshPositions} onRefreshIntraday={refreshIntraday} />;
            })()}
            <div className="grids-container">
              <ContractNoteGrid contractNotes={contractNotes} totalCharges={totalCharges} onRefresh={refreshContractNotes} />
              {(() => {
                const HOLDINGS_PAGE_SIZE = 8;
                const holdingsPages = Math.max(1, Math.ceil(holdings.length / HOLDINGS_PAGE_SIZE));
                const pagedHoldings = holdings.slice((holdingsPage - 1) * HOLDINGS_PAGE_SIZE, holdingsPage * HOLDINGS_PAGE_SIZE);
                return (
                  <div className="holdings-grid-simple">
                    <div className="section-header">
                      <h3>Holdings <span className="count-badge">{holdings.length}</span></h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 700 }}>₹{new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(netBalance)}</span>
                        {holdings.length > 0 && (
                          <span style={{ fontWeight: 600, fontSize: '0.82rem', color: holdingsPnL >= 0 ? '#10b981' : '#ef4444' }}>
                            ({holdingsPnL >= 0 ? '+' : ''}₹{new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(holdingsPnL)})
                          </span>
                        )}
                        <button className="refresh-btn" onClick={refreshHoldings} title="Refresh Holdings">↻</button>
                      </div>
                    </div>
                    {holdings.length === 0 ? <p className="no-data">No holdings</p> : (
                      <>
                        <div className="table-wrap">
                          <table>
                            <thead>
                              <tr>
                                <th>Symbol</th><th>Qty</th><th>Avg Cost</th><th>LTP</th><th>P&L</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pagedHoldings.map((h, i) => {
                                const bookedQty = h.quantity || 0;
                                const t1Qty = h.t1_quantity || 0;
                                const qty = bookedQty + t1Qty;
                                const ltp = holdingLtps[h.tradingsymbol] ?? h.last_price;
                                const pnl = (ltp - h.average_price) * qty;
                                return (
                                  <tr key={i}>
                                    <td className="symbol-cell">{h.tradingsymbol}</td>
                                    <td>
                                      {t1Qty > 0
                                        ? <>{bookedQty} + <span style={{color:'#ef4444',fontWeight:600}}>{t1Qty}</span></>
                                        : bookedQty}
                                    </td>
                                    <td>₹{h.average_price?.toFixed(2)}</td>
                                    <td>₹{ltp?.toFixed(2)}</td>
                                    <td className={pnl >= 0 ? 'profit' : 'loss'}>₹{pnl?.toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {holdingsPages > 1 && (
                          <div className="section-footer">
                            <span className="page-range">
                              {Math.min((holdingsPage - 1) * HOLDINGS_PAGE_SIZE + 1, holdings.length)}–{Math.min(holdingsPage * HOLDINGS_PAGE_SIZE, holdings.length)} of {holdings.length}
                            </span>
                            <div className="pagination">
                              <button disabled={holdingsPage === 1} onClick={() => setHoldingsPage(1)}>«</button>
                              <button disabled={holdingsPage === 1} onClick={() => setHoldingsPage(p => p - 1)}>‹</button>
                              <span className="page-info">{holdingsPage} / {holdingsPages}</span>
                              <button disabled={holdingsPage === holdingsPages} onClick={() => setHoldingsPage(p => p + 1)}>›</button>
                              <button disabled={holdingsPage === holdingsPages} onClick={() => setHoldingsPage(holdingsPages)}>»</button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </>
        )}
        {activeTab === 'algos'          && <AlgosPage />}
        {activeTab === 'analysis'       && <AnalysisPage />}
        {activeTab === 'administration' && activeSubTab === 'downloads'      && <DownloadsPage />}
        {activeTab === 'administration' && activeSubTab === 'configurations' && <ConfigPage holdings={holdings} />}
        {activeTab === 'administration' && activeSubTab === 'logs'           && <LogsPage />}
        {activeTab === 'profile'        && <ProfilePage kiteProfile={portfolio} />}
      </div>

      {showAutoPilotModal && (
        <AutoPilotModal
          onClose={() => setShowAutoPilotModal(false)}
          onStarted={handleAutoPilotStarted}
        />
      )}

      {showReauthPrompt && (
        <div className="modal-overlay" onClick={() => setShowReauthPrompt(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Kite Session Expired</h2>
            <p>Your Kite Connect session has expired. Due to SEBI compliance requirements, you need to re-authenticate every trading day.</p>
            <div className="modal-actions">
              <button className="btn-primary" onClick={() => { handleLogout(); setShowReauthPrompt(false); }}>
                Re-login to Kite
              </button>
              <button className="btn-secondary" onClick={() => setShowReauthPrompt(false)}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {testResult && (
        <div className="modal-overlay" onClick={() => setTestResult(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{testResult.success ? '✅ Token Valid' : '❌ Token Invalid'}</h2>
            <div className="test-result-body">
              <p><strong>Status:</strong> {testResult.message}</p>
              {testResult.tokenValid && <p><strong>Token:</strong> ✅ Valid</p>}
              {!testResult.tokenValid && <p><strong>Token:</strong> ❌ Invalid or Expired</p>}
              {testResult.quotesFetched && <p><strong>Quotes:</strong> ✅ Can fetch data</p>}
              {testResult.quotesFetched === false && <p><strong>Quotes:</strong> ❌ Cannot fetch data</p>}
              {testResult.testData && (
                <div className="test-data">
                  <p><strong>Test Symbol:</strong> {testResult.testData.symbol}</p>
                  <p><strong>Last Price:</strong> ₹{testResult.testData.lastPrice}</p>
                </div>
              )}
              {testResult.error && (
                <p className="error-text"><strong>Error:</strong> {testResult.error}</p>
              )}
            </div>
            <div className="modal-actions">
              {!testResult.success && (
                <button className="btn-primary" onClick={() => { handleLogout(); setTestResult(null); }}>
                  Re-authenticate
                </button>
              )}
              <button className="btn-secondary" onClick={() => setTestResult(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
